import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

// Renders detailed package traffic statistics charts for the security services package log.
export default class extends Controller {
  static targets = ["weeklyChart", "statusChart", "recipientsChart", "couriersChart"]
  static values = { dataUrl: String }

  connect() {
    this.charts = []
    this.loadData()
  }

  disconnect() {
    this.destroyCharts()
  }

  async loadData() {
    try {
      const response = await fetch(this.dataUrlValue, {
        headers: { Accept: "application/json" }
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      this.renderCharts(data)
    } catch (error) {
      console.error("Failed to load package statistics:", error)
    }
  }

  destroyCharts() {
    this.charts.forEach((chart) => chart.destroy())
    this.charts = []
  }

  renderCharts(data) {
    this.destroyCharts()

    if (this.hasWeeklyChartTarget && data.weekly) {
      this.charts.push(this.buildWeeklyChart(this.weeklyChartTarget, data.weekly))
    }
    if (this.hasStatusChartTarget && data.status_breakdown) {
      this.charts.push(this.buildStatusChart(this.statusChartTarget, data.status_breakdown))
    }
    if (this.hasRecipientsChartTarget && data.top_recipients) {
      this.charts.push(this.buildHorizontalBar(this.recipientsChartTarget, data.top_recipients, "rgba(13, 110, 253, 0.7)"))
    }
    if (this.hasCouriersChartTarget && data.top_couriers) {
      this.charts.push(this.buildHorizontalBar(this.couriersChartTarget, data.top_couriers, "rgba(25, 135, 84, 0.7)"))
    }
  }

  buildWeeklyChart(canvas, weekly) {
    return new Chart(canvas.getContext("2d"), {
      type: "bar",
      data: {
        labels: weekly.labels,
        datasets: [
          {
            label: canvas.dataset.deliveredLabel || "Delivered",
            data: weekly.delivered,
            backgroundColor: "rgba(13, 110, 253, 0.7)",
            borderColor: "rgba(13, 110, 253, 1)",
            borderWidth: 1
          },
          {
            label: canvas.dataset.pickedUpLabel || "Picked up",
            data: weekly.picked_up,
            backgroundColor: "rgba(25, 135, 84, 0.7)",
            borderColor: "rgba(25, 135, 84, 1)",
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: { legend: { position: "top" } },
        scales: {
          x: { ticks: { maxRotation: 45, minRotation: 45 } },
          y: { beginAtZero: true, ticks: { precision: 0 } }
        }
      }
    })
  }

  buildStatusChart(canvas, breakdown) {
    return new Chart(canvas.getContext("2d"), {
      type: "doughnut",
      data: {
        labels: breakdown.labels,
        datasets: [
          {
            data: breakdown.data,
            backgroundColor: ["rgba(255, 193, 7, 0.8)", "rgba(25, 135, 84, 0.8)"],
            borderColor: ["rgba(255, 193, 7, 1)", "rgba(25, 135, 84, 1)"],
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "bottom" } }
      }
    })
  }

  buildHorizontalBar(canvas, dataset, color) {
    return new Chart(canvas.getContext("2d"), {
      type: "bar",
      data: {
        labels: dataset.labels,
        datasets: [
          {
            label: canvas.dataset.countLabel || "Count",
            data: dataset.data,
            backgroundColor: color,
            borderWidth: 1
          }
        ]
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { x: { beginAtZero: true, ticks: { precision: 0 } } }
      }
    })
  }
}
